import { StreakFlame } from './StreakFlame';

const CIRCLE_SIZE = 104;
const STROKE = 11;
const RADIUS = (CIRCLE_SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

function CircularProgress({ earned, max }: { earned: number; max: number }) {
  const progress = max > 0 ? Math.min(earned / max, 1) : 0;
  const dashOffset = CIRCUMFERENCE * (1 - progress);
  const cx = CIRCLE_SIZE / 2;
  const cy = CIRCLE_SIZE / 2;
  const isComplete = max > 0 && progress >= 1;

  return (
    <div
      className={isComplete ? 'progress-ring-complete' : undefined}
      style={{ position: 'relative', width: CIRCLE_SIZE, height: CIRCLE_SIZE, flexShrink: 0 }}
    >
      <svg
        width={CIRCLE_SIZE}
        height={CIRCLE_SIZE}
        style={{
          transform: 'rotate(-90deg)',
          filter: isComplete ? 'drop-shadow(0 0 8px oklch(82% 0.15 85 / 0.7))' : undefined,
        }}
      >
        <circle cx={cx} cy={cy} r={RADIUS} fill="none" stroke="oklch(100% 0 0 / 0.22)" strokeWidth={STROKE} />
        <circle
          cx={cx} cy={cy} r={RADIUS} fill="none"
          stroke={isComplete ? 'var(--gold)' : 'oklch(98% 0.02 80)'}
          strokeWidth={STROKE}
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.7s cubic-bezier(0.16, 1, 0.3, 1), stroke 0.3s ease' }}
        />
      </svg>
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        lineHeight: 1, gap: '2px',
      }}>
        <span style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 800,
          fontSize: '1.9rem',
          letterSpacing: '-0.03em',
          color: isComplete ? 'var(--gold)' : '#fff',
        }}>{earned}</span>
        {max > 0 && (
          <span style={{ fontSize: '0.7rem', opacity: 0.8, fontWeight: 700 }}>
            {isComplete ? 'pts' : `/ ${max} pts`}
          </span>
        )}
      </div>
    </div>
  );
}

const DAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

function MiniBarChart({ data }: { data: { date: string; points: number; maxPoints: number }[] }) {
  const today = new Date().toISOString().split('T')[0];

  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: '52px' }}>
      {data.map((d, i) => {
        const isToday = d.date === today;
        const effectiveMax = d.maxPoints > 0 ? d.maxPoints : d.points;
        const isEmpty = effectiveMax === 0;
        const rate = !isEmpty ? Math.min(d.points / effectiveMax, 1) : 0;
        const pct = rate * 100;
        const isNegative = d.points < 0;
        const isComplete = !isEmpty && rate >= 1;
        const dow = new Date(`${d.date}T00:00:00`).getDay();
        const barColor = isEmpty
          ? 'oklch(100% 0 0 / 0.18)'
          : isNegative
            ? 'oklch(70% 0.18 25)'
            : isComplete
              ? 'var(--gold)'
              : 'oklch(98% 0.02 80 / 0.85)';
        return (
          <div
            key={d.date}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', height: '100%' }}
          >
            <div style={{ flex: 1, width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
              <div
                title={`${d.date}: ${d.points} / ${d.maxPoints} pts`}
                style={{
                  height: isEmpty ? '4px' : `${Math.max(pct, 9)}%`,
                  background: barColor,
                  borderRadius: '4px',
                  transition: 'height 0.45s cubic-bezier(0.16, 1, 0.3, 1)',
                  transformOrigin: 'bottom center',
                  animation: `bar-grow 0.45s cubic-bezier(0.16, 1, 0.3, 1) ${i * 45}ms both`,
                  boxShadow: isToday && !isEmpty ? '0 0 8px oklch(98% 0.02 80 / 0.5)' : undefined,
                }}
              />
            </div>
            <span style={{ fontSize: '0.6rem', fontWeight: 800, opacity: isToday ? 1 : 0.55 }}>{DAY_LETTERS[dow]}</span>
          </div>
        );
      })}
    </div>
  );
}

interface PointsWidgetProps {
  earnedToday: number;
  maxToday: number;
  streak: number;
  /** Longest streak ever reached; shown as a subtle "best" hint while on a run. */
  bestStreak?: number;
  weeklyData: { date: string; points: number; maxPoints: number }[];
  loading?: boolean;
}

const SKELETON = 'skeleton-pulse 1.4s ease-in-out infinite';
const SKELETON_BG = 'oklch(100% 0 0 / 0.18)';

function HeroSkeleton() {
  return (
    <div className="hero-card" aria-busy="true">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <span className="hero-eyebrow">Today</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', marginBottom: '1.1rem' }}>
        <div style={{ width: 104, height: 104, borderRadius: '50%', border: `11px solid ${SKELETON_BG}`, flexShrink: 0, animation: SKELETON }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ width: '72%', height: '1.4rem', borderRadius: 6, background: SKELETON_BG, animation: SKELETON }} />
          <div style={{ width: '45%', height: '0.85rem', borderRadius: 6, background: SKELETON_BG, marginTop: '0.55rem', animation: SKELETON }} />
        </div>
      </div>
      <div className="hero-eyebrow" style={{ marginBottom: '0.5rem' }}>Last 7 days</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: '52px' }}>
        {[40, 65, 30, 80, 50, 70, 45].map((h, i) => (
          <div key={i} style={{ flex: 1, height: `${h}%`, borderRadius: 4, background: SKELETON_BG, animation: SKELETON }} />
        ))}
      </div>
    </div>
  );
}

export function PointsWidget({ earnedToday, maxToday, streak, bestStreak, weeklyData, loading }: PointsWidgetProps) {
  if (loading) return <HeroSkeleton />;

  // Nudge that a personal best is within reach (only while on a live run).
  const showBest = streak > 0 && (bestStreak ?? 0) > streak;

  const isComplete = maxToday > 0 && earnedToday >= maxToday;
  const remaining = Math.max(maxToday - earnedToday, 0);
  const headline = maxToday === 0
    ? 'Nothing due today'
    : isComplete
      ? 'Goal smashed! 🎉'
      : earnedToday === 0
        ? "Let's get started"
        : `${remaining} pt${remaining === 1 ? '' : 's'} to go`;

  return (
    <div className="hero-card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <span className="hero-eyebrow">Today</span>
        {streak > 0 && (
          <span
            className="streak-badge"
            title={showBest ? `${streak}-day streak · best ${bestStreak} days` : `${streak}-day streak`}
          >
            <StreakFlame className="streak-flame" />
            {streak} day{streak === 1 ? '' : 's'}
            {showBest && <span className="streak-best">best {bestStreak}</span>}
          </span>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', marginBottom: '1.1rem' }}>
        <CircularProgress earned={earnedToday} max={maxToday} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: '1.4rem', lineHeight: 1.1, letterSpacing: '-0.02em' }}>
            {headline}
          </div>
          {maxToday > 0 && !isComplete && (
            <div style={{ fontSize: '0.85rem', opacity: 0.85, marginTop: '0.25rem' }}>
              {earnedToday} of {maxToday} points earned
            </div>
          )}
          {isComplete && (
            <div style={{ fontSize: '0.85rem', opacity: 0.9, marginTop: '0.25rem' }}>
              Every chore done today. Nice work.
            </div>
          )}
        </div>
      </div>

      <div style={{ position: 'relative' }}>
        <div className="hero-eyebrow" style={{ marginBottom: '0.5rem' }}>Last 7 days</div>
        <MiniBarChart data={weeklyData} />
      </div>
    </div>
  );
}
